import {
  Activity,
  ArrowLeftRight,
  Cpu,
  Database,
  Download,
  GitBranch,
  LifeBuoy,
  Lock,
  MapPin,
  Server,
  Users,
} from "lucide-react";
import { defaultTotalCapacity } from "@woven/contracts";
import { useState } from "react";
import { Link } from "react-router";
import { OpenSourceBanner } from "../components/marketing/OpenSourceBanner";
import { NodeField } from "../components/ui/NodeField";
import type { Interest } from "../components/FoundersClubModal";
import { CapacityTooltip } from "../features/capacity/CapacityTooltip";

type Billing = "monthly" | "annual";

interface Tier {
  id: Interest;
  name: string;
  badge?: { label: string; className: string };
  monthlyPrice: number | null;
  pricePrefix?: string;
  pricePeriod?: string;
  blurb: string;
  features: { icon: typeof Server; label: string; capacity?: number }[];
  cta: { label: string; variant: "primary" | "ghost" };
  footnote?: string;
  highlighted?: boolean;
}

const LITE_CAPACITY = defaultTotalCapacity("lite");
const PRO_CAPACITY = defaultTotalCapacity("pro");

const TIERS: Tier[] = [
  {
    id: "lite",
    name: "Lite",
    badge: { label: "Recommended", className: "badge--accent" },
    monthlyPrice: 0,
    pricePeriod: "forever",
    blurb: "Build and prototype on the shared fabric.",
    features: [
      { icon: Server, label: "Flexible shared servers" },
      {
        icon: Users,
        label: `${LITE_CAPACITY.toLocaleString("en-US")} capacity units`,
        capacity: LITE_CAPACITY,
      },
      { icon: Download, label: "2 GB data out / month" },
      { icon: Database, label: "Ephemeral state" },
      { icon: LifeBuoy, label: "Community support" },
    ],
    cta: { label: "Start Building", variant: "primary" },
    footnote: "Capacity is shared across all your servers.",
    highlighted: true,
  },
  {
    id: "pro",
    name: "Pro",
    badge: { label: "Preview", className: "badge--mock" },
    monthlyPrice: 25,
    pricePeriod: "/ month",
    blurb: "Ship real products with room to grow.",
    features: [
      { icon: Server, label: "Flexible shared servers" },
      {
        icon: Users,
        label: `${PRO_CAPACITY.toLocaleString("en-US")} capacity units`,
        capacity: PRO_CAPACITY,
      },
      { icon: Download, label: "50 GB data out / month" },
      { icon: Database, label: "1 GB persistent state" },
      { icon: Activity, label: "30-day metrics" },
      { icon: Cpu, label: "BYOK inference routing" },
    ],
    cta: { label: "Join Waitlist", variant: "ghost" },
    footnote: "Linear capacity pricing: $0.01 per CU/month ($1 per 100 CU).",
  },
  {
    id: "dedicated",
    name: "Dedicated",
    badge: { label: "Preview", className: "badge--mock" },
    monthlyPrice: null,
    blurb: "Single-tenant infrastructure with reserved capacity.",
    features: [
      { icon: Cpu, label: "Dedicated Woven process" },
      { icon: Lock, label: "Private configuration" },
      { icon: MapPin, label: "Regional placement" },
      { icon: GitBranch, label: "Version controls" },
      { icon: ArrowLeftRight, label: "Usage-based data transfer" },
    ],
    cta: { label: "Join Waitlist", variant: "ghost" },
  },
];

const COMPARE_ROWS: { label: string; lite: string; pro: string; dedicated: string }[] = [
  { label: "Shared servers", lite: "Within capacity", pro: "Within capacity", dedicated: "—" },
  {
    label: "Capacity",
    lite: `${LITE_CAPACITY.toLocaleString("en-US")} units`,
    pro: `${PRO_CAPACITY.toLocaleString("en-US")} units`,
    dedicated: "Custom",
  },
  { label: "Data out / month", lite: "2 GB", pro: "50 GB", dedicated: "Usage-based" },
  { label: "Persistent state", lite: "Ephemeral", pro: "1 GB", dedicated: "Reserved capacity" },
  { label: "Metrics retention", lite: "—", pro: "30 days", dedicated: "Custom" },
  { label: "Inference", lite: "—", pro: "BYOK routing", dedicated: "BYOK routing" },
  {
    label: "Regional placement",
    lite: "Shared fabric",
    pro: "Shared fabric",
    dedicated: "Dedicated region",
  },
  { label: "Support", lite: "Community", pro: "Community", dedicated: "Priority" },
];

function tierPrice(tier: Tier, billing: Billing): number | "TBD" {
  if (tier.monthlyPrice === null) {
    return "TBD";
  }
  if (billing === "monthly" || tier.monthlyPrice === 0) {
    return tier.monthlyPrice;
  }
  return Math.round(tier.monthlyPrice * 0.8);
}

export function PricingPage() {
  const [billing, setBilling] = useState<Billing>("monthly");

  return (
    <div className="page">
      <div className="pricing-hero">
        <div className="pricing-hero-field" aria-hidden="true">
          <NodeField />
        </div>
        <div className="pricing-hero-content">
          <div className="pricing-toggle" role="group" aria-label="Billing interval">
            <button
              type="button"
              className={
                billing === "monthly"
                  ? "pricing-toggle-option pricing-toggle-option--active"
                  : "pricing-toggle-option"
              }
              aria-pressed={billing === "monthly"}
              onClick={() => setBilling("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={
                billing === "annual"
                  ? "pricing-toggle-option pricing-toggle-option--active"
                  : "pricing-toggle-option"
              }
              aria-pressed={billing === "annual"}
              onClick={() => setBilling("annual")}
            >
              Annual
            </button>
            <span className="pricing-save-badge">Save 20%</span>
          </div>

          <h1 className="pricing-title">
            Infrastructure that <span className="hero-title-accent">grows with you.</span>
          </h1>
          <p className="page-lede pricing-lede">
            Start free on the shared fabric. Add capacity, state, and dedicated infrastructure when
            you need it.
          </p>
        </div>
      </div>

      <div className="tier-grid">
        {TIERS.map((tier) => (
          <article
            className={
              tier.highlighted ? "panel tier-card tier-card--highlighted" : "panel tier-card"
            }
            key={tier.id}
          >
            <div className="tier-card-header">
              <h2 className="tier-name">{tier.name}</h2>
              {tier.badge ? (
                <span className={`badge ${tier.badge.className}`}>{tier.badge.label}</span>
              ) : null}
            </div>

            <p className="tier-price">
              {tier.pricePrefix ? (
                <span className="tier-price-prefix">{tier.pricePrefix}</span>
              ) : null}
              {tier.monthlyPrice !== null ? <span className="tier-price-currency">$</span> : null}
              <span className="tier-price-amount">{tierPrice(tier, billing)}</span>
              {tier.pricePeriod ? (
                <span className="tier-price-period">{tier.pricePeriod}</span>
              ) : null}
            </p>
            {billing === "annual" && tier.monthlyPrice !== null && tier.monthlyPrice > 0 ? (
              <p className="tier-price-note">billed annually</p>
            ) : null}

            <p className="tier-blurb">{tier.blurb}</p>

            <ul className="tier-features">
              {tier.features.map((feature) => (
                <li key={feature.label} className="tier-feature">
                  <feature.icon
                    size={16}
                    strokeWidth={1.75}
                    absoluteStrokeWidth
                    aria-hidden="true"
                  />
                  <span>
                    {feature.label}
                    {feature.capacity !== undefined ? (
                      <>
                        {" "}
                        <CapacityTooltip
                          label={`How ${tier.name} capacity is calculated`}
                          capacity={feature.capacity}
                        />
                      </>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>

            {tier.footnote ? <p className="tier-footnote">{tier.footnote}</p> : null}
            <Link
              to="/sign-up"
              state={{ initialInterest: tier.id }}
              className={
                tier.cta.variant === "primary"
                  ? "btn btn--primary btn--block"
                  : "btn btn--ghost btn--block"
              }
            >
              {tier.cta.label}
            </Link>
          </article>
        ))}
      </div>

      <OpenSourceBanner />

      <div className="panel compare-panel">
        <h2 className="panel-title">Compare plans</h2>
        <div className="compare-table-wrap">
          <table className="data-table compare-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">
                  Lite <span className="badge badge--accent">Recommended</span>
                </th>
                <th scope="col">
                  Pro <span className="badge badge--mock">Preview</span>
                </th>
                <th scope="col">
                  Dedicated <span className="badge badge--mock">Preview</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="compare-row-label">
                    {row.label}{" "}
                    {row.label === "Capacity" ? <CapacityTooltip capacity={LITE_CAPACITY} /> : null}
                  </th>
                  <td>{row.lite}</td>
                  <td>{row.pro}</td>
                  <td>{row.dedicated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
